import { isFunction, isNumber, isObjectLike, isString, type Members } from './guards';
import type { Child, Dynamic, Instruction, MaybeDynamic, PropInput, Reader, SignalLike, SigValue } from './types';

/** Thrown when an instruction is built or rendered in a way the contract forbids. */
export class InstructionError extends Error {
  override readonly name = 'InstructionError';
}

export function isSignalLike<T>(value: T): value is Members<T, SignalLike<unknown>> {
  return isObjectLike(value) && 'get' in value && typeof value.get === 'function';
}

/** A reader is a function that takes no arguments; a function with parameters is a row template, not a reader. */
export function isDynamic<T>(value: T): value is Members<T, Dynamic<unknown>> {
  return (typeof value === 'function' && value.length === 0) || isSignalLike(value);
}

export function toReader<T>(value: Dynamic<T>): Reader<T> {
  return isFunction(value) ? value : () => value.get();
}

/**
 * A prop that may be a signal, a reader, or a plain value, as one reader.
 *
 * A component takes `MaybeDynamic<T>` for anything that can change, so a caller may hand
 * it a signal and keep writing to it; a plain value reads as a constant.
 */
export function reader<T>(value: MaybeDynamic<T>): Reader<T> {
  if (isDynamic(value)) {
    // SAFETY: `isDynamic` is the test every binding uses — a zero-argument function or
    // a `get()`. A data prop is neither, so what it matched is this prop's own reader.
    const dynamic = value as Dynamic<T>;
    return toReader(dynamic);
  }
  // SAFETY: the branch above took every reader and signal, so what is left is the value.
  const constant = value as T;
  return () => constant;
}

export function isInstruction<T>(value: T): value is T & Instruction {
  return isObjectLike(value) && 't' in value && typeof value.t === 'string';
}

/**
 * The children a `sig` reader yielded, or null when its value is text.
 *
 * A template that composes another component (`{Button(props)}` in TSX, which
 * the tsrx pass thunks) reads an instruction here, and the renderers put its
 * subtree between the markers instead of the text of an object. A DOM node a
 * caller built is adopted through the same bridge `normaliseChildren` uses.
 */
export function instructionChildren(value: SigValue): readonly Instruction[] | null {
  // Text, the common case, is settled before any object test.
  if (!isObjectLike(value)) return null;
  if (isInstruction(value)) return [value];
  if (isDomNode(value)) return [{ t: 'dom', node: value }];
  if (!Array.isArray(value) || value.length === 0) return null;
  const children: Instruction[] = [];
  for (const item of value) {
    if (isInstruction(item)) children.push(item);
    else if (isDomNode(item)) children.push({ t: 'dom', node: item });
    else return null;
  }
  return children;
}

/**
 * A child reader as a `sig` reads it, so any `Child` may be a reader's value.
 *
 * Text, the common case, passes straight through. A reader or signal it yields
 * is read in turn (a hole `{content}` whose `content` is itself a reader would
 * otherwise print the function's source); a list holding anything but
 * instructions and nodes is normalised as static children are; and `true` and
 * `false` write nothing, as they do as static children, so `{open && <p/>}`
 * never prints "false".
 */
export function childReader(read: Reader<unknown>): Reader<SigValue> {
  return () => readChild(read);
}

/** One read of a child reader, as `childReader` makes it: for a renderer that reads once and keeps no reader. */
export function readChild(read: Reader<unknown>): SigValue {
  let value = read();
  if (isString(value) || isNumber(value)) return value;
  while (isDynamic(value)) value = toReader(value)();
  if (value === true || value === false) return null;
  if (Array.isArray(value) && value.length > 0 && instructionChildren(value) === null) return normaliseChildren(value);
  // SAFETY: readers are read through, booleans dropped and loose lists normalised
  // above; what a child reader yields beyond those is text, a subtree, a node or
  // nothing, which is what a `sig` renders.
  return value as SigValue;
}

/** True for a live DOM node; false where no DOM exists, so the server never adopts one by accident. */
export function isDomNode<T>(value: T): value is T & Node {
  // The object test comes first: looking up a global the server lacks is slow, and text is never a node.
  return isObjectLike(value) && typeof Node !== 'undefined' && value instanceof Node;
}

/** Adjacent text merges into one node, as a browser parses it, so hydration claims one text node per run. */
function pushText(out: Instruction[], value: string): void {
  const last = out[out.length - 1];
  if (last?.t === 'text') out[out.length - 1] = { t: 'text', value: last.value + value };
  else out.push({ t: 'text', value });
}

function isTextChild(child: Child): child is string | number | bigint {
  return isString(child) || typeof child === 'number' || typeof child === 'bigint';
}

/** Strings and numbers become text (adjacent runs merged), dynamics become `sig`, DOM nodes become `dom`, arrays flatten, `false`/`true`/`null`/`undefined` vanish. */
export function normaliseChildren(children: readonly Child[]): Instruction[] {
  const out: Instruction[] = [];
  const push = (child: Child): void => {
    if (child === null || child === undefined || child === true || child === false) {
      return;
    }
    if (isTextChild(child)) {
      pushText(out, String(child));
      return;
    }
    if (Array.isArray(child)) {
      for (const inner of child) push(inner);
      return;
    }
    if (isInstruction(child)) {
      out.push(child);
      return;
    }
    if (isDomNode(child)) {
      out.push({ t: 'dom', node: child });
      return;
    }
    if (isDynamic(child)) {
      out.push({ t: 'sig', read: childReader(isFunction(child) ? child : () => child.get()) });
      return;
    }
    if (isFunction(child)) {
      throw new InstructionError(
        'A child function must take no parameters; row templates only belong inside each()',
      );
    }
    throw new InstructionError('Unsupported child: expected text, an instruction, a DOM node or a reader');
  };
  for (const child of children) push(child);
  return out;
}

/**
 * Whether a row of a list needs `c:row` markers around it: only when it starts
 * or ends with bare text, which a browser would otherwise run together with
 * the text of the row beside it. Every other row is found by position.
 */
export function rowNeedsMarkers(children: readonly Instruction[]): boolean {
  return children[0]?.t === 'text' || children[children.length - 1]?.t === 'text';
}

/**
 * A template's attribute reader is used as the compiler gave it, and the
 * compiler wraps every expression: a reader passed down as a prop arrives as a
 * reader of that reader, and is read through here.
 */
export function readThrough(value: PropInput): PropInput {
  let current = value;
  while (isPropReader(current)) current = current();
  return current;
}

/** A zero-argument function among a prop's values is its reader; a handler takes its event. */
function isPropReader(value: PropInput): value is Reader<PropInput> {
  return isFunction(value) && value.length === 0;
}
